import { Directive, ElementRef, HostListener, Input, NgZone, OnDestroy } from '@angular/core';

/**
 * Converts comma keystrokes to periods on number inputs.
 * Fixes mobile numpad keyboards in Spanish/European locales where
 * the decimal separator is a comma, but type="number" rejects it.
 *
 * Also caps the decimal places. On inputs that expose the selection (type="text") the next value is predicted and
 * the keystroke is blocked before it lands. type="number" does not expose it (`selectionStart` is null), so there the
 * edit is checked once applied (see `onInputCapture`).
 */
@Directive({
    selector: '[finaDecimalInput]',
})
export class DecimalInputDirective implements OnDestroy {
    @Input('finaDecimalInput') decimalPlaces: number | string = 5;

    /** Valor previo a la edición en curso de un campo sin selección legible: a él se vuelve si la edición sobra decimales. */
    private valueBeforeInput: string | null = null;
    /** El pegado con coma se inserta con execCommand (sincrónico): durante esa llamada, lo que sobre se recorta. */
    private pasting = false;
    /** Listener de captura de `input`, creado en la primera edición (ver `watchAppliedEdits`). */
    private inputCapture: ((event: Event) => void) | null = null;

    constructor(private readonly elementRef: ElementRef<HTMLInputElement>, private readonly ngZone: NgZone) {}

    ngOnDestroy(): void {
        if (this.inputCapture) this.elementRef.nativeElement.removeEventListener('input', this.inputCapture, true);
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        if (event.key === ',') {
            event.preventDefault();
            const input = event.target as HTMLInputElement;
            // En un type="number" no hay selección legible: el punto entra y `onInputCapture` lo revisa ya aplicado, igual
            // que en `onBeforeInput` (predecir con el cursor al final podía tragarse una coma válida).
            if (!this.canReadSelection(input) || this.allowsNextValue(input, '.')) {
                input.dispatchEvent(new KeyboardEvent('keydown', { key: '.', code: 'Period', bubbles: true }));
                // execCommand dispara su propio `beforeinput` con «.»: en un type="number" `onBeforeInput` lo deja pasar y
                // `onInputCapture` lo revisa ya aplicado, sin efecto porque `allowsNextValue` acaba de admitirlo.
                document.execCommand('insertText', false, '.');
            }
        }
    }

    @HostListener('beforeinput', ['$event'])
    onBeforeInput(event: InputEvent): void {
        const input = event.target as HTMLInputElement;

        if (event.data === ',') {
            event.preventDefault();
            if (this.allowsNextValue(input, '.')) {
                document.execCommand('insertText', false, '.');
            }
            return;
        }

        if (!this.canReadSelection(input)) {
            // type="number": sin la selección no se sabe cómo queda el valor (seleccionar todo y teclear lo reemplaza),
            // así que la edición pasa y `onInputCapture` la revisa ya aplicada.
            this.watchAppliedEdits();
            this.valueBeforeInput = input.value;
            return;
        }

        if (event.data && !this.allowsNextValue(input, event.data)) {
            event.preventDefault();
        }
    }

    @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent): void {
        const input = event.target as HTMLInputElement;
        const pastedValue = event.clipboardData?.getData('text') || '';

        if (!this.canReadSelection(input)) {
            // El navegador pega sobre la selección real y `onInputCapture` recorta lo que sobre. Con coma se inserta
            // ya convertida, también sobre la selección: type="number" no acepta la coma.
            // execCommand está deprecado, pero es la única vía que inserta sobre la selección real de un type="number"
            // y conserva el deshacer del navegador (setRangeText no aplica a ese tipo). Salida, si un navegador lo
            // retira: pasar estos campos a type="text" con inputmode="decimal", donde la selección sí es legible.
            this.watchAppliedEdits();
            if (pastedValue.includes(',')) {
                event.preventDefault();
                this.pasting = true;
                try {
                    document.execCommand('insertText', false, pastedValue.replace(',', '.'));
                } finally {
                    this.pasting = false;
                }
            }
            return;
        }

        const nextValue = this.getNextValue(input, pastedValue.replace(',', '.'));

        if (this.hasAllowedDecimalPlaces(nextValue)) {
            return;
        }

        event.preventDefault();
        input.value = this.truncateDecimalPlaces(nextValue);
        input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    /**
     * Revisa la edición ya aplicada. Pegar (o soltar) recorta lo que sobra, como siempre se hizo al pegar; sin un valor
     * previo al que volver (campo vacío, o una edición que no pasó por beforeinput) también se recorta, para no borrar lo
     * escrito. Una tecla que deja decimales de más no entra, igual que en los campos de texto: el campo vuelve al valor
     * previo y el evento no sigue, así que ni el modelo ni los (input) de la plantilla la ven.
     *
     * Desde la primera edición corre en cada `input` del campo, también en las que no pueden pasarse del límite: en un
     * type="number" no hay selección legible para saberlo antes. Lo habitual es salir en el primer `return`.
     */
    private onInputCapture(event: Event): void {
        const host = this.elementRef.nativeElement;
        const previous = this.valueBeforeInput;
        this.valueBeforeInput = null;
        if (this.hasAllowedDecimalPlaces(host.value)) return;

        const inputType = (event as InputEvent).inputType;
        if (this.pasting || inputType === 'insertFromPaste' || inputType === 'insertFromDrop' || !previous) {
            host.value = this.truncateDecimalPlaces(host.value);
            return;
        }

        host.value = previous;
        event.stopImmediatePropagation();
    }

    /**
     * Engancha `onInputCapture` en la primera edición del campo, no al crearlo: Precios de venta pinta miles de campos
     * y la mayoría nunca se edita (un listener por campo desde el inicio se notaba en memoria y en el scroll). En
     * captura sobre el propio campo corre antes que ngModel, formControl y los (input) de la plantilla, que así nunca
     * ven un valor con decimales de más. Fuera de Angular: revisar el valor no cambia ningún binding.
     *
     * Que se registre después que ngModel no cambia el orden: desde whatwg/dom#686, en el propio elemento (AT_TARGET)
     * los listeners de captura corren antes que los de burbuja, sin importar el orden de registro. Lo cumplen Chrome 89,
     * Firefox 119 y Safari desde WebKit r236002, todos anteriores a los navegadores que soporta el build. Lo fija la
     * prueba «ngModel nunca recibe el valor…» del spec: sin captura, falla.
     */
    private watchAppliedEdits(): void {
        if (this.inputCapture) return;
        this.inputCapture = (event: Event) => this.onInputCapture(event);
        this.ngZone.runOutsideAngular(() => this.elementRef.nativeElement.addEventListener('input', this.inputCapture, true));
    }

    /** type="number" devuelve null en `selectionStart` (y navegadores viejos lanzan): ahí no se puede predecir. */
    private canReadSelection(input: HTMLInputElement): boolean {
        try {
            return input.selectionStart !== null && input.selectionStart !== undefined;
        } catch {
            return false;
        }
    }

    private allowsNextValue(input: HTMLInputElement, insertedValue: string): boolean {
        return this.hasAllowedDecimalPlaces(this.getNextValue(input, insertedValue));
    }

    private getNextValue(input: HTMLInputElement, insertedValue: string): string {
        const selectionStart = input.selectionStart ?? input.value.length;
        const selectionEnd = input.selectionEnd ?? selectionStart;
        return `${input.value.slice(0, selectionStart)}${insertedValue}${input.value.slice(selectionEnd)}`;
    }

    private hasAllowedDecimalPlaces(value: string): boolean {
        const decimalPart = value.split('.')[1];
        return !decimalPart || decimalPart.length <= this.maxDecimalPlaces;
    }

    private truncateDecimalPlaces(value: string): string {
        const [integerPart, decimalPart] = value.split('.');
        if (typeof decimalPart === 'undefined') {
            return value;
        }

        return `${integerPart}.${decimalPart.slice(0, this.maxDecimalPlaces)}`;
    }

    private get maxDecimalPlaces(): number {
        // Uso como atributo pelado (`finaDecimalInput`, sin valor): Angular pasa '' al @Input y
        // Number('') es 0 — que aquí significaría "cero decimales", no el default. Bloqueaba el
        // segundo decimal en todos los inputs de montos (el primero se cuela porque type="number"
        // reporta value='' con el parcial inválido "12.").
        if (this.decimalPlaces === '' || this.decimalPlaces === null || this.decimalPlaces === undefined) {
            return 5;
        }
        const parsedDecimalPlaces = Number(this.decimalPlaces);
        return Number.isInteger(parsedDecimalPlaces) && parsedDecimalPlaces >= 0 ? parsedDecimalPlaces : 5;
    }
}
